<script setup lang="ts">
import {
  formatting,
  LegacyDelivery,
  LegacyOrder,
  legacyOrderCommands,
  legacyOrderKey,
  LegacyOrderRow,
  useContext
} from '@hantera/portal-app';
import {computed, inject, ref} from 'vue';
import ProductSelector from '@/views/LegacyOrder/ProductSelector.vue';
import {CatalogSearchHit} from '@/backend/catalog';
import useBackend from '@/composition/useBackend';
import Decimal from 'decimal.js';
import {v4 as uuidv4} from 'uuid';
import {ContextMenuSections} from "@hantera/design-system";

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));

const props = defineProps<{
  delivery: LegacyDelivery
  orderRow: LegacyOrderRow
}>()

const context = useContext();
const backend = useBackend();
const { currency } = formatting;

const imageUrl = computed(() => {
  return context.serverUrl() + '/legacy' + props.orderRow.item.image;
});

const canEditProduct = computed(() => {
  return props.delivery?.state === 'Open';
})

function productSelected(product: CatalogSearchHit | undefined) {
  if (!props.delivery || !product) {
    return;
  }

  backend.legacyOrders.buildCommandGroup(
    order.value.orderId,
    '{oldProductNumer} to {newProductNumber}', {
    oldProductNumer: props.orderRow.item.articleNumber,
    newProductNumber: product.productNumber,
  }).command(legacyOrderCommands.updateRowQuantity, {
    rowId: props.orderRow.rowId,
    newQuantity: new Decimal(0),
  }).command(legacyOrderCommands.addRow, {
    deliveryId: props.delivery.deliveryId,
    articleNumber: product.productNumber,
    newRowId: uuidv4(),
    quantity: props.orderRow.quantity,
  }).send();
}

const productContext = computed(() => {
  
  let productName = props.orderRow.item.name;
  if (productName.length > 35) {
    productName = productName.substring(0, 30) + '...'
  }
    
  return [[{
    label: `Copy ${props.orderRow.item.articleNumber}`,
    action: () => navigator.clipboard.writeText(props.orderRow.item.articleNumber)
  },{
    label: `Copy ${productName}`,
    action: () => navigator.clipboard.writeText(props.orderRow.item.name)
  }]] as ContextMenuSections;
})

</script>

<template>
  <div class="legacy-order-row-product" v-context-menu="productContext">
    <div class="legacy-order-row-product__image box-content">
      <img :src="imageUrl" />
    </div>
    <ProductSelector
      :can-edit="canEditProduct"
      :product-number="orderRow.item.articleNumber"
      @selected="productSelected"
      :inventory-id="delivery.inventoryId"
    />

    <div class="legacy-order-row-product__identity">
      <div
        class="legacy-order-row-product__name"
        :title="orderRow.item.name"
      >{{ orderRow.item.name }}</div>
    </div>



  </div>
</template>

<style>
.legacy-order-row-product {
  display: grid;
  grid-template-columns: 40px auto;
  grid-template-rows: 20px 20px;
  grid-auto-rows: 16px;
  column-gap: 5px;
  row-gap: 2px;
  white-space: nowrap;

  line-height: 16px;
}

.legacy-order-row-product__image {
  position: relative;
  grid-column: 1;
  grid-row: 1 / 3;
}

.legacy-order-row-product__image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.legacy-order-row-product__identity {
  display: grid;
  grid-template-columns: minmax(auto, max-content) min-content;
  padding-right: 5px;
  align-items: center;
}

.legacy-order-row-product__name {
  overflow: hidden;
  flex-shrink: 1;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.legacy-order-row-product__listing-price {
  padding-left: 5px;
  opacity: .5;
}

@container (max-width: 700px) {
  .legacy-order-row-product {
    grid-column: 1 / -2;
  }
}
</style>